import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FormControl } from './ui/form'
import { AudioLines } from 'lucide-react'
import { useRef, useState } from 'react'
import { useAxiosQuery } from '@/hooks/useAxiosQuery'
import { Bot } from '@db8/shared'

export const VoiceSelector = ({
  onValueChange,
  defaultValue,
  disabled,
  preferredGender,
  preferredName,
  autoSelectIfEmpty = true,
}: {
  onValueChange: (val: string) => void
  defaultValue?: any
  disabled?: boolean
  preferredGender?: 'female' | 'male'
  preferredName?: string
  autoSelectIfEmpty?: boolean
}) => {
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({})
  const [playingVoice, setPlayingVoice] = useState<string | null>(null)
  const hoverTimers = useRef<{ [key: string]: number | null }>({})

  const { data: bots = [] } = useAxiosQuery<Bot[]>({
    url: '/bots',
  })

  const getSampleUrl = (voice: string) => {
    const safeKey = voice.replace(/:/g, '-')
    return `/audioSamples/${safeKey}.wav`
  }

  const playSample = (voice: string) => {
    stopAllSamples()
    const sample = audioRefs.current[voice]
    if (sample) {
      sample.currentTime = 0
      const p = sample.play()
      if (p && typeof p.catch === 'function') {
        p.catch((err: any) => {
          const msg = String(err?.message || '')
          if (err?.name === 'AbortError' || msg.includes('interrupted by a call to pause()')) {
            return
          }
          // Silently ignore NotAllowed when no user gesture
          if (msg.includes("didn't interact with the document")) return
          // no-op
        })
      }
      setPlayingVoice(voice)
      sample.onended = () => setPlayingVoice(null)
    }
  }

  const stopAllSamples = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      audio.pause()
      audio.currentTime = 0
    })
    setPlayingVoice(null)
  }

  const renderVoiceItem = (bot: Bot) => {
    const { voice, name, imageUrl, id } = bot
    const sampleUrl = getSampleUrl(voice)
    const imagePath = imageUrl ? `/bots/${imageUrl}` : '/bots/default.png'

    if (!audioRefs.current[voice]) {
      audioRefs.current[voice] = new Audio(sampleUrl)
    }

    const isPlaying = playingVoice === voice

    return (
      <SelectItem
        key={bot.id}
        value={bot.id.toString()}
        disabled={disabled}
        className="cursor-pointer text-sm"
      >
        <div className="flex items-center justify-between pr-2 w-full">
          <div className="flex items-center gap-2">
            <img src={imagePath} alt={name} className="w-6 h-6 rounded-full object-cover" />
            <span>{name}</span>
          </div>
          <button
            type="button"
            onMouseEnter={(e) => {
              e.stopPropagation()
              // debounce hover to avoid rapid enter/leave causing AbortError
              if (hoverTimers.current[voice]) {
                window.clearTimeout(hoverTimers.current[voice] as number)
                hoverTimers.current[voice] = null
              }
              hoverTimers.current[voice] = window.setTimeout(() => {
                playSample(voice)
              }, 120)
            }}
            onMouseLeave={(e) => {
              e.stopPropagation()
              if (hoverTimers.current[voice]) {
                window.clearTimeout(hoverTimers.current[voice] as number)
                hoverTimers.current[voice] = null
              }
              stopAllSamples()
            }}
            className="ml-2"
          >
            <AudioLines
              className={`transition-colors ${
                isPlaying ? 'text-green-500' : 'text-muted-foreground'
              }`}
              size="18px"
            />
          </button>
        </div>
      </SelectItem>
    )
  }

  const femaleBots = bots.filter((bot) => bot.gender === 'female')
  const maleBots = bots.filter((bot) => bot.gender === 'male')

  // Auto-select a preferred bot when empty
  // Only triggers when no defaultValue is set by parent
  // Priority: preferredName -> preferredGender
  if (autoSelectIfEmpty && bots.length > 0) {
    const hasValue = Boolean(
      defaultValue !== undefined && defaultValue !== null && String(defaultValue) !== '' &&
        // support object shape with id
        (typeof defaultValue !== 'object' || (defaultValue as any)?.id !== undefined),
    )

    if (!hasValue) {
      let candidate: Bot | undefined
      if (preferredName) {
        const nameLc = preferredName.toLowerCase()
        candidate = bots.find((b) => b.name?.toLowerCase() === nameLc)
      }
      if (!candidate && preferredGender) {
        candidate = bots.find((b) => b.gender === preferredGender)
      }
      if (candidate) {
        // Inform parent so form state updates and flows back as defaultValue
        onValueChange(String(candidate.id))
      }
    }
  }

  return (
    <Select
      onValueChange={(value) => {
        stopAllSamples()
        onValueChange(value)
      }}
      defaultValue={defaultValue}
    >
      <FormControl>
        <SelectTrigger className="text-sm">
          {(() => {
            const defaultString = defaultValue?.id ? defaultValue?.id : defaultValue
            const selectedBot = bots.find((bot) => bot.id.toString() === defaultString?.toString())
            if (!selectedBot) return <span className="text-muted-foreground">Select a voice</span>

            const imagePath = selectedBot.imageUrl
              ? `/bots/${selectedBot.imageUrl}`
              : '/bots/default.png'

            return (
              <div className="flex items-center gap-2">
                <img
                  src={imagePath}
                  alt={selectedBot.name}
                  className="w-5 h-5 rounded-full object-cover"
                />
                <span>{selectedBot.name}</span>
              </div>
            )
          })()}
        </SelectTrigger>
      </FormControl>

      <SelectContent>
        {femaleBots.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs text-muted-foreground">Female Voices</div>
            {femaleBots.map((bot) => renderVoiceItem(bot))}
          </>
        )}
        {femaleBots.length > 0 && maleBots.length > 0 && <SelectSeparator />}
        {maleBots.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs text-muted-foreground">Male Voices</div>
            {maleBots.map((bot) => renderVoiceItem(bot))}
          </>
        )}
      </SelectContent>
    </Select>
  )
}
